import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useAiThinkingStatus } from '../hooks/useAiThinkingStatus';
import { useSmoothedStreamText } from '../hooks/useSmoothedStreamText';
import { getCanvasMessages } from '../utils/canvasI18n';

const MAX_THOUGHT_CHARS = 1200;

function phaseLabel(phase, language) {
  const messages = getCanvasMessages(language).thinkingLive || {};
  if (phase === 'preparing') return messages.preparing || 'Preparing…';
  if (phase === 'building') return messages.building || 'Building suggestions…';
  if (phase === 'thinking') return messages.thinking || 'Thinking…';
  return messages.thinking || 'Thinking…';
}

function splitReplyParagraphs(content) {
  const text = String(content ?? '');
  if (!text) return [];
  const parts = text
    .split(/\n\s*\n/)
    .map(part => part.replace(/^\n+|\n+$/g, ''))
    .filter(part => part.length > 0);
  return parts.length > 0 ? parts : [text];
}

const BoardAiThinking = ({
  active,
  variant = 'context',
  className = '',
  language = 'en',
  liveThought = '',
  thinkingPhase = '',
  liveReply = '',
}) => {
  const stagedMessage = useAiThinkingStatus(active, variant, language);
  const thoughtRef = useRef(null);
  const smoothedReply = useSmoothedStreamText(liveReply, { enabled: active });
  const smoothedThought = useSmoothedStreamText(liveThought, {
    enabled: active && !liveReply,
  });

  const displayThought = useMemo(() => {
    const text = String(smoothedThought || '').trim();
    if (!text) return '';
    if (text.length <= MAX_THOUGHT_CHARS) return text;
    return `…${text.slice(-MAX_THOUGHT_CHARS)}`;
  }, [smoothedThought]);

  const replyParagraphs = useMemo(
    () => (smoothedReply ? splitReplyParagraphs(smoothedReply) : []),
    [smoothedReply]
  );

  useEffect(() => {
    const el = thoughtRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [displayThought, smoothedReply]);

  if (!active) return null;

  const hasLive = Boolean(displayThought || smoothedReply);
  const header = hasLive
    ? phaseLabel(thinkingPhase || (smoothedReply ? 'building' : 'thinking'), language)
    : stagedMessage;

  if (!header && !hasLive) return null;

  return (
    <Box className={`board-ai-thinking ${className}`.trim()} role="status" aria-live="polite">
      <Box className="board-ai-thinking-header">
        <Typography variant="caption" component="span" className="board-ai-thinking-text">
          {header}
        </Typography>
      </Box>
      {displayThought && !smoothedReply ? (
        <Box
          ref={thoughtRef}
          component="pre"
          className="board-ai-thinking-live"
        >
          {displayThought}
        </Box>
      ) : null}
      {replyParagraphs.length > 0 ? (
        <Typography
          variant="caption"
          component="div"
          className="board-ai-thinking-reply board-ai-thinking-reply--typing board-ai-thinking-reply--indented"
        >
          {replyParagraphs.map((paragraph, index) => (
            <p key={index} className="board-ai-thinking-reply-paragraph">
              {paragraph}
              {index === replyParagraphs.length - 1 ? (
                <span className="board-ai-thinking-caret" aria-hidden />
              ) : null}
            </p>
          ))}
        </Typography>
      ) : null}
    </Box>
  );
};

export default BoardAiThinking;
